export default {
  title: "Knowledge Base",
  description: "Manage and search knowledge items",

  // Search
  searchPlaceholder: "Search content and titles...",
  searchResults: "Search results",
  noResults: "No results found",
  resultsCount: "{count} results",

  // Filters
  filters: "Filters",
  filterByType: "Filter by type",
  filterByStatus: "Filter by status",
  filterByDepartment: "Filter by department",
  filterByStream: "Filter by stream",
  filterByOwner: "Filter by owner",
  allTypes: "All types",
  allStatuses: "All statuses",
  allDepartments: "All departments",
  allStreams: "All streams",
  allOwners: "All owners",
  resetFilters: "Reset filters",

  // Sorting
  sortBy: "Sort by",
  sortNewest: "Newest",
  sortOldest: "Oldest",
  sortPriority: "Priority",
  sortConfidence: "Confidence",
  sortTitle: "Title",

  // Card display
  showMore: "Show more",
  showLess: "Show less",
  createdAt: "Created",
  updatedAt: "Updated",
  author: "Author",
  confidence: "Confidence",
  priority: "Priority",
  type: "Type",
  departments: "Departments",
  streams: "Streams",
  tags: "Tags",

  // Status badges
  draft: "Draft",
  classified: "Classified",
  reviewed: "Reviewed",
  approved: "Approved",
  locked_to_sot: "Locked SOT",
  deprecated: "Deprecated",
  archived: "Archived",
  rejected: "Rejected",

  // Confidence badges
  low: "Low",
  medium: "Medium",
  high: "High",
  verified: "Verified",

  // Priority badges
  critical: "Critical",
  priorityHigh: "High",
  normal: "Normal",

  // Owner domains
  case_preparation: "Case Preparation",
  sales: "Sales",
  recruitment: "Recruitment",
  finance: "Finance",
  systems: "Systems",
  management: "Management",

  // Actions
  addNew: "Add new item",
  edit: "Edit",
  delete: "Delete",
  export: "Export",
  refresh: "Refresh",
  viewDetails: "View details",

  // Detail view
  fullContent: "Full content",
  metadata: "Metadata",
  lifecycle: "Lifecycle",
  sourceInfo: "Source info",
  relations: "Relations",
  linkedItems: "Linked items",
  useCases: "Use cases",
  lenses: "Lenses",
  reviewInfo: "Review info",
  needsResolution: "Needs resolution",
  validUntil: "Valid until",
  reviewDue: "Review due",
  sotLevel: "SOT Level",
  visibility: "Visibility",

  // Add new form
  newKnowledgeItem: "New knowledge item",
  titleRequired: "Title required",
  contentRequired: "Content required",
  selectType: "Select type",
  selectDepartments: "Select departments",
  selectStreams: "Select streams",
  selectUseCases: "Select use cases",
  selectLenses: "Select lenses",
  selectOwner: "Select owner",
  tagsPlaceholder: "Add comma-separated tags...",
  save: "Save",
  cancel: "Cancel",
  saving: "Saving...",

  // Visibility options
  internal: "Internal",
  ai_internal: "AI Internal",
  ai_external: "AI External",
  published: "Published",

  // Stats
  totalItems: "Total items",
  draftCount: "Drafts",
  approvedCount: "Approved",
  needsReview: "Needs review",
  expiringSoon: "Expiring soon",

  // Messages
  loadingItems: "Loading knowledge items...",
  errorLoading: "Error loading items",
  itemSaved: "Item saved successfully",
  itemDeleted: "Item deleted successfully",
  errorSaving: "Error saving item",
  confirmDelete: "Are you sure you want to delete this item?",

  // ─── Research Library ───
  research: {
    title: "Research Library",
    subtitle: "External information, advisory only — it does not become internal law on its own",
    verdictEmpty: "The library has no sources yet",
    verdictDue: "{count} sources need a re-check",
    verdictUndecided: "{count} sources are awaiting a decision",
    verdictClear: "{count} sources in the library — all current and decided",
    lensMissing: "The research lens is not installed in the database yet",
    lensMissingHint: "Run migration 20260827120000_research_library_seed, then refresh this page",
    empty: "No sources match the current filters",
    emptyHint: "Clear the filters or add a new source to the library",
    loading: "Loading sources...",
    errorLoading: "Failed to load sources",
    errorLoadingHint: "Check the database connection and refresh",
    searchPlaceholder: "Search titles and content...",
    kpiSources: "Sources",
    kpiUndecided: "Awaiting a decision",
    kpiDue: "Need a check",
    kpiDomains: "Domains",
    openSource: "Open source",
    unsafeUrl: "Address blocked from display",
    checkedAt: "Checked",
    neverChecked: "Never checked",
    purpose: "Used for",
    audience: "Written for",
    allDomains: "All domains",
    allAuthorities: "All authority types",
    allStances: "All decisions",
    allFreshness: "All check states",
    authority: {
      official: "Official docs",
      framework: "Conceptual framework",
      learning_map: "Learning map",
      community: "Community experience",
    },
    stance: {
      adopt: "Adopt",
      reject: "Reject",
      watch: "Watch",
      have_already: "Have already",
      partial: "Partial",
      undecided: "Undecided",
    },
    freshness: {
      fresh: "Current",
      due: "Due",
      overdue: "Overdue",
      unknown: "Unknown",
    },
  },

  // ─── Knowledge Sources shelf ───
  sources: {
    title: "Knowledge Sources",
    subtitle: "One source, and beside it everything actually harvested from it",
    loading: "Loading the shelf...",
    errorLoading: "Failed to load the shelf",
    errorLoadingHint: "Check the database connection and refresh",
    searchPlaceholder: "Search title, author and notes...",
    verdictEmpty: "No sources on the shelf yet",
    verdictOrphans: "{count} knowledge items are not linked to any source",
    verdictWaiting: "{count} items are waiting for the approval gate",
    verdictEmptyShelves: "{count} sources are registered but nothing was harvested from them",
    verdictClear: "{sources} sources and {items} items — all linked and approved",
    verdictTruncated: "Result was truncated — the counts below are partial",
    kpiSources: "Sources",
    kpiItems: "Knowledge items",
    kpiWaiting: "Waiting for gate",
    kpiOrphans: "Orphan items",
    allKinds: "All source kinds",
    allTrust: "All trust levels",
    allGates: "All gate states",
    noMatch: "No sources match the filters",
    noMatchHint: "Clear the filters to see the full shelf",
    truncatedTitle: "The shelf is larger than one fetch",
    truncatedHint: "Only 1000 items were fetched. Chunk counts, orphan counts and the verdict do not reflect the full picture",
    orphanTitle: "Some items have no source",
    orphanHint: "{count} items are not linked to any shelf row, so their origin is unknown",
    harvested: "{items} things learned from this source",
    harvestedChunks: "out of {chunks} original text chunks",
    fillsGap: "Fills gap",
    notes: "Notes",
    nothingHarvested: "Nothing has been harvested from this source yet",
    origin: "Origin row",
    sotLevel: "Level {level}",
    untypedGroup: "Unclassified",
    pickSource: "Pick a source from the list to read what was learned from it",
    pickSourceHint: "The reader stays put while you scroll the list",
    closeReader: "Close reader",
    sourceId: "Instance id",
    copyId: "Click to copy the id",
    copied: "Copied",
    showOrigin: "Show the original text",
    hideOrigin: "Hide the original text",
    originTitle: "The text this item was written from",
    originLoading: "Loading the original text...",
    originMissing: "No origin text was stored for this item",
    originError: "Loading the origin text failed. This is an error, not a missing origin",
    originInvalid: "Origin reference is invalid or unsupported and was not fetched",
    addSource: "Add a source",
    addSourceTitle: "New source for the shelf",
    addSourceHint: "The source is registered empty. Harvesting from it is a separate step",
    formTitle: "Source title",
    formSlug: "Latin identifier",
    formSlugHint: "Latin letters and hyphens only, unique on the shelf",
    formAuthor: "Author",
    formYear: "Year",
    formUrl: "Link",
    formKind: "Source kind",
    formPriority: "Priority",
    formTrust: "Trust level",
    formFillsGap: "Which gap it fills",
    formNotes: "Notes",
    formSubmit: "Save the source",
    formCancel: "Cancel",
    formSaving: "Saving...",
    formRequired: "Title and Latin identifier are required",
    formDuplicate: "That identifier is already taken on the shelf",
    priority: {
      now: "Now",
      soon: "Soon",
      someday: "Someday",
    },
    kind: {
      book: "Book",
      article: "Article",
      framework: "Framework",
      internal_wiki: "Internal wiki",
      gam_experience: "GAM experience",
      ai_conversation: "AI conversation",
      external_reference: "External reference",
    },
    trust: {
      authoritative: "Authoritative",
      advisory: "Advisory",
    },
    gate: {
      waiting: "Waiting for approval",
      approved: "Approved",
      empty: "Empty",
    },
    status: {
      draft: "Draft",
      classified: "Classified",
      reviewed: "Reviewed",
      approved: "Approved",
      locked_to_sot: "Locked to SoT",
      deprecated: "Deprecated",
      archived: "Archived",
      rejected: "Rejected",
    },
  },
} as const;
